import { Bot, MailCheck, Palette, type LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Reveal } from '../components/Reveal';

interface Pillar {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  body: string;
}

/**
 * Why-SendFast section — expands the three pillars promised in the Hero
 * subhead ("On-brand. Inbox-perfect. Agent-native.") into three benefit cards.
 *
 * Differentiates SendFast from Klaviyo / Mailchimp / Resend + glue code, by
 * leading with what's hardest about marketing email today (brand consistency,
 * cross-client rendering, agent integration) rather than with feature lists.
 */
const PILLARS: Pillar[] = [
  {
    icon: Palette,
    eyebrow: 'On-brand',
    title: 'Knows your brand.',
    body:
      'Upload your guidelines once — palette, voice, common offers. Every email comes out yours, without you re-explaining the tone or pasting the right hex code again.',
  },
  {
    icon: MailCheck,
    eyebrow: 'Inbox-perfect',
    title: 'Lands in every inbox.',
    body:
      'Inline-styled tables, MSO-tested, dark-mode-safe. Renders identically in Gmail, Apple Mail, and the corporate Outlook your buyer is still forced to use.',
  },
  {
    icon: Bot,
    eyebrow: 'Agent-native',
    title: 'Driven by any AI agent.',
    body:
      'Open MCP. Drop SendFast into Claude, Cursor, ChatGPT — let your agent draft, edit, screenshot, and ship campaigns. Five tools, no glue code.',
  },
];

export function WhySendFast() {
  return (
    <section id="why" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <Reveal>
          <p className="text-muted-foreground text-xs uppercase tracking-[0.18em]">
            Why SendFast
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display mt-3 max-w-2xl text-4xl font-semibold tracking-tight md:text-5xl">
            Three things that should be default.{' '}
            <span className="text-primary">But aren&apos;t.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <Card className="hover:border-foreground/20 h-full p-7 transition">
                <div className="flex items-center justify-between">
                  <span className="bg-accent text-primary inline-flex h-10 w-10 items-center justify-center rounded-lg">
                    <p.icon className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <span className="text-muted-foreground text-[10px] uppercase tracking-[0.18em]">
                    {p.eyebrow}
                  </span>
                </div>
                <h3 className="font-display mt-6 text-xl font-semibold tracking-tight">
                  {p.title}
                </h3>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                  {p.body}
                </p>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
